  /* eslint-disable react-hooks/exhaustive-deps */
  import React, { useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import TextFeild from "../../components/CommonComponents/TextFeild";
import { INPUT_LABELS } from "../../constants/lang";
import Multiselect from "../../components/CommonComponents/Select/MultipleSelect";
import SearchableSelect from "../../components/CommonComponents/SearchableSelect";
import InfiniteScrollSearchableSelect from "../../components/CommonComponents/InfiniteScrollSearchableSelect";
import { fetchCountrys, fetchCitysByCountry } from "../../store/reducers/authentication/apiThunk";
import { ToastNotifyError } from "../../components/CommonComponents/Toast/ToastNotify";
import rollbar from "../../rollbarConfig";
import StyledSignup from "./StyledSignup";

const categoryOptions = ["Men", "Women", "Neutral"];

const BusinessDetails = ({ control, errors, watch }) => {
  const dispatch = useDispatch();
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptionsByCountry, setCityOptionsByCountry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingMoreCities, setIsLoadingMoreCities] = useState(false);
  const [cityPage, setCityPage] = useState(1);
  const [hasMoreCities, setHasMoreCities] = useState(true);
  const [isSearchingCities, setIsSearchingCities] = useState(false);
  const [isInSearchMode, setIsInSearchMode] = useState(false);
  const cityPageRef = useRef(1); // Use ref to track current page reliably

  useEffect(() => {
    const allCity = async () => {
      if (!selectedCountry) {
        setCityOptionsByCountry([]);
        setCityPage(1);
        cityPageRef.current = 1;
        setHasMoreCities(true);
        return;
      }
      
      try {
        setIsLoadingCities(true);
        const response = await dispatch(fetchCitysByCountry({
          country: selectedCountry,
          page_no: 1,
          page_size: 20
        }));
        console.log('first')
        const { status, data, message } = response.payload;
        if (status) {
          setCityOptionsByCountry(data.results || data);
          setCityPage(1);
          cityPageRef.current = 1;
          // Check if there are more pages based on _meta structure
          const hasMore = response?.payload?._meta && response?.payload?._meta?.current_page < response?.payload?._meta?.total_pages;
          console.log("response?.payload", response?.payload)
          setHasMoreCities(hasMore);
        } else {
          ToastNotifyError(message);
        }
      } catch (error) {
        ToastNotifyError("An error occurred. Please try again.");
        rollbar.error("Error message", error);
      } finally {
        setIsLoadingCities(false);
      }
    };
    allCity();
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    const allCountry = async () => {
      try {
        const response = await dispatch(fetchCountrys());
        const { status, data, message } = response.payload;
        if (status) {
          setCountryOptions(data);
        } else {
          ToastNotifyError(message);
        }
      } catch (error) {
        ToastNotifyError("An error occurred. Please try again.");
        rollbar.error("Error message", error);
      }
    };
    allCountry();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize selectedCountry from form values when component mounts
  useEffect(() => {
    if (watch && watch('country')) {
      setSelectedCountry(watch('country'));
    }
  }, [watch]);

  // Clear city field when country is cleared
  useEffect(() => {
    if (!selectedCountry && control && control.setValue) {
      try {
        control.setValue('city', '');
      } catch (error) {
        console.warn('Could not clear city field:', error);
      }
    }
  }, [selectedCountry, control]);

  // Load city options when country is set from Redux data
  useEffect(() => {
    const loadCityOptionsForExistingData = async () => {
      // Check if we have a country selected but no city options loaded and not in search mode
      if (selectedCountry && cityOptionsByCountry.length === 0 && !isInSearchMode) {
        try {
          setIsLoadingCities(true);
          const response = await dispatch(fetchCitysByCountry({
            country: selectedCountry,
            page_no: 1,
            page_size: 20
          }));
          console.log('second')
          const { status, data, message } = response.payload;
          if (status) {
            setCityOptionsByCountry(data.results || data);
            setCityPage(1);
            cityPageRef.current = 1;
            // Check if there are more pages based on _meta structure
            const hasMore = response?.payload?._meta && response?.payload?._meta?.current_page < response?.payload?._meta?.total_pages;
            setHasMoreCities(hasMore);
          } else {
            ToastNotifyError(message);
          }
        } catch (error) {
          ToastNotifyError("An error occurred. Please try again.");
          rollbar.error("Error message", error);
        } finally {
          setIsLoadingCities(false);
        }
      }
    };

    loadCityOptionsForExistingData();
  }, [selectedCountry, cityOptionsByCountry.length, dispatch, isInSearchMode]);

  // Handle country selection
  const handleCountryChange = (value, option, field) => {
    // Handle case when value is null/undefined (clearing the field)
    if (!value) {
      setSelectedCountry(null);
      setCityOptionsByCountry([]);
      setCityPage(1);
      setHasMoreCities(true);
      setIsInSearchMode(false);
      return;
    }
    
    // Only clear city field if the country is actually changing (not when loading from Redux)
    const isCountryChanging = selectedCountry !== value;
    
    setSelectedCountry(value); // Use the value directly since it's now the name
    
    // Clear city options when country changes
    setCityOptionsByCountry([]);
    setCityPage(1);
    cityPageRef.current = 1;
    setHasMoreCities(true);
    setIsInSearchMode(false);
    
    // Clear city field only when country is actually changing
    if (isCountryChanging && control && control.setValue) {
      control.setValue('city', '');
    }
  };

  // Handle city search
  const handleCitySearch = async (searchTerm, loadMore = false) => {
    
    if (!selectedCountry) return;

    // Prevent multiple concurrent calls
    if (isSearchingCities && !loadMore) {
      return;
    }

    try {
      if (loadMore) {
        setIsLoadingMoreCities(true);
      } else {
        setIsSearchingCities(true);
        setIsLoadingCities(true);
        // Set search mode when performing a search
        if (searchTerm && searchTerm.trim()) {
          setIsInSearchMode(true);
        }
      }

      // Use ref to get the current page value reliably
      const nextPage = loadMore ? cityPageRef.current + 1 : 1;
      
      console.log('City API call:', { 
        country: selectedCountry, 
        search: searchTerm || '', 
        page_no: nextPage, 
        loadMore,
        currentCityPage: cityPage
      });

      const response = await dispatch(fetchCitysByCountry({
        country: selectedCountry,
        search: searchTerm || '',
        page_no: nextPage,
        page_size: 20
      }));

      const { status, data, message } = response.payload;
      
      if (status) {
        const newCities = data.results || data;
        
        console.log('City API response:', { 
          currentPage: data._meta?.current_page, 
          totalPages: data._meta?.total_pages, 
          citiesCount: newCities.length,
          loadMore,
          requestedPage: nextPage
        });
        
        if (loadMore) {
          // Append new cities to existing list
          setCityOptionsByCountry(prev => [...prev, ...newCities]);
          // Update both state and ref
          setCityPage(nextPage);
          cityPageRef.current = nextPage;
          console.log('Updated cityPage to:', nextPage);
        } else {
          // Replace cities with new search results
          setCityOptionsByCountry(newCities);
          setCityPage(1);
          cityPageRef.current = 1;
        }
        
        // Check if there are more pages based on _meta structure
        const hasMore = response?.payload?._meta && response?.payload?._meta?.current_page < response?.payload?._meta?.total_pages;
        console.log('Has more cities:', hasMore, 'Current page:', response?.payload?._meta?.current_page, 'Total pages:', response?.payload?._meta?.total_pages);

        setHasMoreCities(hasMore);
        
      } else {
        ToastNotifyError(message);
      }
    } catch (error) {
      ToastNotifyError("An error occurred. Please try again.");
      rollbar.error("Error message", error);
    } finally {
      if (loadMore) {
        setIsLoadingMoreCities(false);
      } else {
        setIsLoadingCities(false);
        setIsSearchingCities(false);
      }
    }
  };

  return (
    <StyledSignup>
      <Box>
        <Typography className='step-header'>Business Details</Typography>
      </Box>
      <Box>
        <Typography className='step-sub-header'>Business Overview</Typography>
      </Box>

      <Box className='signup-textfeilds'>
        <Box className='signup-store'>
          <Box className='text-feilds'>
            <Controller
              name='Appareltype'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  required
                  id='Appareltype'
                  label={INPUT_LABELS.Appareltype}
                  placeholder='Apparel Type'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.Appareltype}
                  helperText={errors.Appareltype?.message}
                  {...field}
                />
              )}
            />
          </Box>
          <Box className='text-feilds'>
            <Controller
              name='Categories'
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Multiselect
                  name='Categories'
                  id='Categories'
                  required
                  label={INPUT_LABELS.Categories}
                  placeholder='Categories'
                  error={!!errors.Categories}
                  helperText={errors.Categories?.message}
                  options={categoryOptions}
                  onChange={(selectedOptions) =>
                    field.onChange(selectedOptions)
                  }
                  {...field}
                />
              )}
            />
          </Box>
        </Box>
        <Box className='signup-store'>
          <Box className='text-feilds'>
            <Controller
              name='locationStudio'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  {...field}
                  id='locationStudio'
                  label={INPUT_LABELS.locationStudio}
                  placeholder='Location(s) of Design'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.locationStudio}
                  helperText={errors.locationStudio?.message}
                />
              )}
            />
          </Box>
          <Box className='text-feilds'>
            <Controller
              name='ManufacturLocation'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  id='ManufacturLocation'
                  {...field}
                  required
                  label={INPUT_LABELS.ManufacturLocation}
                  placeholder='Manufacturing Location'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.ManufacturLocation}
                  helperText={errors.ManufacturLocation?.message}
                />
              )}
            />
          </Box>
        </Box>
        <Box className='signup-store'>
          <Box className='text-feilds-2'>
            <Controller
              name='inventoryStorage'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  {...field}
                  id='inventoryStorage'
                  required
                  label={INPUT_LABELS.inventoryStorage}
                  placeholder='Inventory Storage Location'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.inventoryStorage}
                  helperText={errors.inventoryStorage?.message}
                />
              )}
            />
          </Box>
        </Box>
        <Box>
          <Typography className='step-sub-header'>Business Address</Typography>
        </Box>
        <Box className='signup-store'>
          <Box className='text-feilds'>
            <Controller
              name='streetAddress'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  id='streetAddress'
                  required
                  label={INPUT_LABELS.streetAddress}
                  placeholder='Street Address'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.streetAddress}
                  helperText={errors.streetAddress?.message}
                  {...field}
                />
              )}
            />
          </Box>
          <Box className='text-feilds'>
            <Controller
              name='country'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <SearchableSelect
                  options={countryOptions}
                  value={field.value}
                  onChange={(value, option) => {
                    field.onChange(value);
                    handleCountryChange(value, option, field);
                  }}
                  placeholder='Select Country'
                  label={INPUT_LABELS.country}
                  error={errors.country?.message}
                  helperText={errors.country?.message}
                  displayKey="name"
                  valueKey="name"
                  searchKey="name"
                  required
                />
              )}
            />
          </Box>
          
        </Box>
        <Box className='signup-store'>
        <Box className='text-feilds'>
            <Controller
              name='city'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <InfiniteScrollSearchableSelect
                  options={cityOptionsByCountry}
                  value={field.value}
                  onChange={(value, option) => field.onChange(value)}
                  onSearch={handleCitySearch}
                  placeholder='Select City'
                  label={INPUT_LABELS.city}
                  error={errors.city?.message}
                  helperText={errors.city?.message}
                  displayKey="name"
                  valueKey="name"
                  searchKey="name"
                  loading={isLoadingCities}
                  loadingMore={isLoadingMoreCities}
                  hasMore={hasMoreCities}
                  disabled={!selectedCountry}
                  required
                />
              )}
            />
          </Box>
          <Box className='text-feilds'>
            <Controller
              name='zipCode'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <TextFeild
                  {...field}
                  id='zipCode'
                  required
                  label={INPUT_LABELS.zipCode}
                  placeholder='Zip/Postal Code'
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  error={!!errors.zipCode}
                  helperText={errors.zipCode?.message}
                  type='number'
                  onKeyDown={(e) => {
                    if (e.key === "." || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                />
              )}
            />
          </Box>
    
        </Box>
      </Box>
    </StyledSignup>
  );
};

export default BusinessDetails;
